import React, {
  DragEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { faBookOpen, faCog, faGlobeEurope, faSpinner, faThLarge } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import Link from "next/link";

import Reactions from "@/components/reactions";
import { UserPill, UserPillUser } from "@/components/user-pill/user-pill";
import {
  Animation,
  AnimationListItemFieldsFragment,
  AnimationType,
  PixelMode,
  User,
} from "@/gql/generated/graphql";
import { useModal } from "@/hooks/use-modal";
import config from "@/lib/config";
import getAnimationThumbnail from "@/lib/getAnimationThumbnail";

import DailyAnimationsModal from "../modal/components/daily-animations";

const MAX_STACK_SIZE = 2;

export type ListItemAnimation = AnimationListItemFieldsFragment & {
  public?: Animation["public"];
  dailyAnimationCount?: Animation["dailyAnimationCount"];
  user?: UserPillUser;
  altThumbnail?: string | null;
  commentCount?: number;
  dateUpdated?: string;
};

type AnimationListItemProps = {
  animation: ListItemAnimation;
  showPublic: boolean;
  user?: User;
  onAnimationSelected?: (id: Animation["id"]) => void;
};

export const AnimationListItem = ({
  animation,
  showPublic,
  user,
  onAnimationSelected,
}: AnimationListItemProps) => {
  const [loaded, setLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const { openModal } = useModal();
  const userData = user ?? animation.user;

  const dailyAnimationCount = animation.dailyAnimationCount ?? 0;

  const thumbPath = getAnimationThumbnail({ animation });

  useEffect(() => {
    if (imgRef.current && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, []);

  const showEncoding = animation.url && !animation.encoded;
  const showSpinner = !loaded && !showEncoding;

  const renderStack = (stackSize: number) => {
    return Array.from(
      Array(stackSize < MAX_STACK_SIZE ? stackSize : MAX_STACK_SIZE).keys(),
    ).map((i) => <div className="animation-item__stack" key={i} />);
  };

  const onOpenDailyAnimations = (
    e: MouseEvent<HTMLAnchorElement>,
    username: string,
    date: string,
  ) => {
    e.preventDefault();
    openModal(() => <DailyAnimationsModal username={username} date={date} />);
  };

  const clickHandler = (e: MouseEvent<HTMLAnchorElement>) => {
    if (
      dailyAnimationCount > 1 &&
      animation?.user?.name &&
      animation.dateUpdated
    ) {
      onOpenDailyAnimations(e, animation.user.name, animation.dateUpdated);
    } else if (onAnimationSelected) {
      onAnimationSelected(animation.id);
    }
  };

  const pagePart =
    animation.type === AnimationType.Standalone ? "animation" : "comic";

  const dragstartHandler = (ev: DragEvent<HTMLLIElement>) => {
    ev.dataTransfer.setData(
      "text/uri-list",
      `https://theanimator.co.uk/animation/${animation.url}`,
    );
    ev.dataTransfer.setData("text/animation-url", animation.url);
    ev.dataTransfer.setData("text/animation-id", `${animation.id}`);
    ev.dataTransfer.dropEffect = "move";
    setIsDragging(true);
    ev.dataTransfer.setDragImage(ev.currentTarget, 0, 0);
  };

  const dragendHandler = (ev: DragEvent<HTMLLIElement>) => {
    setIsDragging(false);
  };

  return (
    <li
      className={`animation-list__item ${isDragging ? "opacity-20" : ""}`}
      draggable
      onDragStart={dragstartHandler}
      onDragEnd={dragendHandler}
    >
      {dailyAnimationCount > 1 ? renderStack(dailyAnimationCount - 1) : null}
      <div className="aspect-4/3 overflow-hidden bg-white shadow-md size-full relative">
        <Link
          href={animation.url ? `/${pagePart}/${animation.url}` : "#"}
          className="inline-block hover:no-underline size-full relative"
          onClick={clickHandler}
          prefetch={false}
        >
          {thumbPath && animation.encoded && (
            <Image
              className={`transition duration-500 ease-in-out object-cover ${!loaded ? 'opacity-0 scale-125' : 'opacity-100 scale-100'}`}
              alt={animation.title}
              src={thumbPath}
              onLoad={() => setLoaded(true)}
              placeholder="empty"
              ref={imgRef}
              fill
              style={{ imageRendering: "pixelated" }}
              sizes="(max-width: 539px) 450px, 260px"
            />
          )}

          {showSpinner && (
            <div className="flex text-gray-500 justify-center items-center size-full">
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
          )}

          {showEncoding && (
            <div className="flex flex-col text-gray-500 justify-center items-center size-full text-lg text-gray-400">
              <div><FontAwesomeIcon icon={faCog} spin /></div>
              <div>Encoding...</div>
            </div>
          )}
        </Link>
        <div className="animation-item__title-bar absolute top-0 w-full bg-white/60 text-sm p-1 backdrop-blur-md wrap-break-word">
          <div>
            <div>
              <Link
                href={`/${pagePart}/${animation.url}`}
                className="animation-list__item-link font-bold"
                onClick={clickHandler}
                prefetch={false}
              >
                {animation.title}{" "}
                {dailyAnimationCount > 1 ? (
                  <span className="text-sm shadow-md inline-block py-0.5 px-2 leading-none text-center whitespace-nowrap align-baseline rounded-full bg-gray-400 text-white">
                    x{`${dailyAnimationCount}`}
                  </span>
                ) : null}
              </Link>
            </div>
            <div className="text-xs mt-0.5 mb-0.5">
              <Reactions
                reactions={animation.reactions}
                comments={animation.commentCount}
              />
            </div>
            {showPublic && animation.public ? (
              <span className="text-xs shadow-md inline-block py-0.5 px-2 leading-none text-center whitespace-nowrap align-baseline rounded-full bg-orange-400 text-black mr-1">
                <FontAwesomeIcon icon={faGlobeEurope} /> In Gallery
              </span>
            ) : null}
            {animation.type === AnimationType.Comic ? (
              <span className="text-xs shadow-md inline-block py-0.5 px-2 leading-none text-center whitespace-nowrap align-baseline rounded-full bg-green-200 text-black mr-1">
                <FontAwesomeIcon icon={faBookOpen} /> Comic
              </span>
            ) : null}
          </div>
        </div>
        <div className="text-sm absolute bottom-0 left-0 m-1">
          {userData && <UserPill user={userData} />}
        </div>
      </div>
    </li>
  );
};
